/**
 * i18n.js — lichte internationalisatielaag voor de frontend.
 *
 * Ontwerpprincipes (spiegelt de backend `i18n_engine.py`):
 * - Ondersteunde talen: NL en EN. Uitbreidbaar door in `locales/` een nieuw
 *   JSON-bestand toe te voegen en te registreren in `CATALOGS`.
 * - Fallback-keten: gekozen taal -> DEFAULT_LANGUAGE (nl) -> sleutel zelf.
 * - Sleutels zijn dot-notation (bv. `auth.login_failed`).
 * - Placeholders in de vorm `{name}` worden vervangen door `params.name`.
 * - Taalvoorkeur wordt in localStorage bewaard onder key `bs_language` en
 *   gesynchroniseerd wanneer de user (na login) een nieuwe voorkeur krijgt.
 *
 * De taal wordt niet automatisch naar de backend teruggeschreven — dat gebeurt
 * expliciet vanuit het instellingen-scherm zodat we intent (bewust wijzigen)
 * scheiden van sessie-init (na login).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import nl from './locales/nl.json';
import en from './locales/en.json';

export const SUPPORTED_LANGUAGES = ['nl', 'en'];
export const DEFAULT_LANGUAGE = 'nl';
const STORAGE_KEY = 'bs_language';

const CATALOGS = { nl, en };

/**
 * Normaliseert een taalstring naar een ondersteunde code.
 * "en-US" -> "en", onbekend -> null.
 */
export function normalizeLanguage(value) {
  if (!value || typeof value !== 'string') return null;
  const cleaned = value.trim().toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGUAGES.includes(cleaned) ? cleaned : null;
}

/**
 * Bepaalt initiële taal op basis van (in volgorde):
 * 1. expliciete parameter
 * 2. localStorage
 * 3. browser navigator.language
 * 4. DEFAULT_LANGUAGE
 */
export function resolveInitialLanguage(explicit) {
  const fromExplicit = normalizeLanguage(explicit);
  if (fromExplicit) return fromExplicit;

  try {
    const stored = normalizeLanguage(window.localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch (_) {
    // localStorage kan geblokkeerd zijn (private mode) — silently negeren.
  }

  if (typeof navigator !== 'undefined') {
    const nav = normalizeLanguage(navigator.language);
    if (nav) return nav;
    if (Array.isArray(navigator.languages)) {
      for (const lang of navigator.languages) {
        const n = normalizeLanguage(lang);
        if (n) return n;
      }
    }
  }
  return DEFAULT_LANGUAGE;
}

/** Interne helper — dotted-key lookup in een catalog. */
function lookup(catalog, key) {
  if (!catalog || !key) return undefined;
  const parts = key.split('.');
  let node = catalog;
  for (const part of parts) {
    if (node && typeof node === 'object' && part in node) {
      node = node[part];
    } else {
      return undefined;
    }
  }
  return typeof node === 'string' ? node : undefined;
}

/** Vervangt {placeholders} in een template met waardes uit params. */
function interpolate(template, params) {
  if (!params || typeof template !== 'string') return template;
  return template.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match
  );
}

/**
 * Vertaalt een sleutel voor een specifieke taal.
 * Fallback: gevraagde taal -> DEFAULT_LANGUAGE -> sleutel zelf.
 * Bij een missing key wordt (alleen in dev) een console.warn gelogd.
 */
export function translate(key, lang, params) {
  const primary = CATALOGS[lang] ? lookup(CATALOGS[lang], key) : undefined;
  if (primary !== undefined) return interpolate(primary, params);

  if (lang !== DEFAULT_LANGUAGE) {
    const fallback = lookup(CATALOGS[DEFAULT_LANGUAGE], key);
    if (fallback !== undefined) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] Missing key "${key}" for lang "${lang}", using ${DEFAULT_LANGUAGE}.`);
      }
      return interpolate(fallback, params);
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[i18n] Missing key "${key}" for lang "${lang}" (no fallback).`);
  }
  return key;
}

// ---------------------------------------------------------------------------
// React context / hook
// ---------------------------------------------------------------------------

const I18nContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key,
});

export function I18nProvider({ initialLanguage, children }) {
  const [language, setLanguageState] = useState(() =>
    resolveInitialLanguage(initialLanguage)
  );

  // Synchroniseer <html lang="..."> zodat screenreaders en zoekmachines
  // de actuele taal zien.
  useEffect(() => {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = language;
    }
  }, [language]);

  // Persist de keuze zodat een refresh dezelfde taal toont.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch (_) {
      // ignore
    }
  }, [language]);

  const setLanguage = useCallback((next) => {
    const normalized = normalizeLanguage(next);
    if (normalized) setLanguageState(normalized);
  }, []);

  const t = useCallback(
    (key, params) => translate(key, language, params),
    [language]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Hook: `const { t, language, setLanguage } = useTranslation();` */
export function useTranslation() {
  return useContext(I18nContext);
}
